import { useEffect, useState, useRef, useCallback } from "react";
import { Stage, Rect, Layer } from "react-konva";
import useImage from "use-image";

import { Sections } from "./Sections/Sections";
import Seats from "./Seats/Seats";
import useSeatmapStore from "../../../../../../../stores/seatmapStore";
import { useTooltips } from "../providers/TooltipProvider";
import { useActions } from "../providers/ActionsProvider";
import { Spinner } from "../../../../../../LoadingContainer/Spinner";

// by default Konva prevent some events when node is dragging
// it improve the performance and work well for 95% of cases
// we need to enable all events on Konva, even when we are dragging a node
// so it triggers touchmove correctly
window.Konva.hitOnDragEnabled = true;

export default function KonvaSeatmap({ loadingComplete, mapError }) {
  const data = useSeatmapStore((state) => state.data);
  const setScale = useSeatmapStore((state) => state.setScale);
  const background = useSeatmapStore((state) => state.background);
  const { hideTooltip } = useTooltips();
  const { stageRef, originalScaleRef, SCALE_FACTOR, SCALE_LIMITS } =
    useActions();

  const [image, status] = useImage(background?.url);

  const originalWidth = background?.width; // Original content width
  const originalHeight = background?.height; // Original content height
  const aspectRatio = originalWidth / originalHeight;

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Less expensive to redraw canvas is we're not listening
  const [isInteracting, setIsInteracting] = useState(false);
  const [isPanPinching, setIsPanPinching] = useState(false);
  const THROTTLE_INTERVAL = 32; // ms - roughly one frame
  const MAX_WHEEL_DELTA = 100; // Prevent extreme zoom jumps
  const interactionTimeout = useRef(null);
  const wheelTimeout = useRef(null);
  const imageLayerRef = useRef(null);
  const accumulatedDelta = useRef(0);
  const lastWheelTime = useRef(0);
  const isZooming = useRef(false);

  // State for touch interactions
  const [lastCenter, setLastCenter] = useState(null);
  const [lastDist, setLastDist] = useState(0);
  const [dragStopped, setDragStopped] = useState(false);
  // New state to track info for smooth transition from 2 to 1 touch
  const transitionTouchInfo = useRef(null);

  function calculateScalePercentage(scaleToUse) {
    const ratio = scaleToUse / originalScaleRef.current;
    const percentage = (ratio * 100 - 100) / 10;
    return Number(percentage.toFixed(0));
  }

  useEffect(() => {
    const resizeStage = () => {
      const map = document.getElementById("map");
      if (!map) return;

      const containerWidth = map.clientWidth;
      const containerHeight = map.clientHeight;
      const maxByWidth = containerWidth;
      const maxByHeight = containerHeight * aspectRatio; // because we divide by height we multiply here

      let newWidth, newHeight;
      if (maxByWidth < maxByHeight) {
        // If width is the limiting factor
        newWidth = containerWidth;
        newHeight = containerWidth / aspectRatio;
      } else {
        // If height is the limiting factor or if they are equal
        newWidth = containerHeight * aspectRatio;
        newHeight = containerHeight;
      }

      const scaleX = newWidth / originalWidth;
      const scaleY = newHeight / originalHeight;
      const scale = Math.min(scaleX, scaleY) * 0.8;
      originalScaleRef.current = scale;

      const stage = stageRef.current;
      if (!stage) return;

      stage.setAttrs({
        scaleX: scale,
        scaleY: scale,
      });

      setWidth(containerWidth);
      setHeight(containerHeight);
      setScale(scale);

      const adjustedOffsetX = (originalWidth - containerWidth / scale) / 2;
      const adjustedOffsetY = (originalHeight - containerHeight / scale) / 2;
      setOffset({ x: adjustedOffsetX, y: adjustedOffsetY });

      stage.batchDraw();
    };

    if (loadingComplete) {
      resizeStage();
      window.addEventListener("resize", resizeStage);

      return () => window.removeEventListener("resize", resizeStage);
    }
  }, [
    aspectRatio,
    originalHeight,
    originalWidth,
    originalScaleRef,
    setScale,
    stageRef,
    loadingComplete,
  ]);

  // Reset interaction state after delay
  const resetInteractionState = () => {
    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current);
    }

    interactionTimeout.current = setTimeout(() => {
      setIsInteracting(false);
      setIsPanPinching(false);
    }, 150); // Adjust delay as needed
  };

  useEffect(() => {
    if (isInteracting) {
      hideTooltip();
    }
  }, [isInteracting]); // hideTooltip

  const handleWheel = (e) => {
    e.evt.preventDefault();
    setIsInteracting(true);

    // Accumulate the delta, but cap it to prevent extreme values
    accumulatedDelta.current += Math.min(
      Math.max(e.evt.deltaY, -MAX_WHEEL_DELTA),
      MAX_WHEEL_DELTA
    );

    const now = performance.now();

    // If we're not already processing a frame and enough time has passed
    if (
      !isZooming.current &&
      now - lastWheelTime.current >= THROTTLE_INTERVAL
    ) {
      isZooming.current = true;

      wheelTimeout.current = requestAnimationFrame(() => {
        const stage = stageRef.current;
        if (!stage) return;

        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        // Use the accumulated delta for direction
        const direction = accumulatedDelta.current > 0 ? -1 : 1;

        // Scale the zoom factor based on accumulated delta magnitude
        const zoomIntensity = Math.min(
          Math.abs(accumulatedDelta.current) / 50,
          1
        );
        const scaleBy = 1 + (SCALE_FACTOR - 1) * zoomIntensity;

        const mousePointTo = {
          x: (pointer.x - stage.x()) / oldScale,
          y: (pointer.y - stage.y()) / oldScale,
        };

        const newScale =
          direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

        // Cap scale
        const wouldBeScale = calculateScalePercentage(newScale);
        if (
          wouldBeScale > SCALE_LIMITS.MAX ||
          wouldBeScale < SCALE_LIMITS.MIN
        ) {
          if (wheelTimeout.current) {
            cancelAnimationFrame(wheelTimeout.current);
          }
          wheelTimeout.current = null;
          isZooming.current = false;
          accumulatedDelta.current = 0;
          resetInteractionState();
          return false;
        }

        const newPos = {
          x: pointer.x - mousePointTo.x * newScale,
          y: pointer.y - mousePointTo.y * newScale,
        };

        stage.setAttrs({
          scaleX: newScale,
          scaleY: newScale,
          x: newPos.x,
          y: newPos.y,
        });

        stage.batchDraw();
        setScale(newScale);

        // Reset for next frame
        wheelTimeout.current = null;
        isZooming.current = false;
        lastWheelTime.current = now;
        accumulatedDelta.current = 0;

        resetInteractionState();
        return true;
      });
    }
  };

  // Handle drag start
  const handleDragStart = () => {
    setIsInteracting(true);
  };

  // Handle drag end
  const handleDragEnd = () => {
    resetInteractionState();
  };

  // Touch event handlers
  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  const getCenter = (p1, p2) => {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  };

  const handleTouchMove = useCallback(
    (e) => {
      e.evt.preventDefault();
      setIsInteracting(true);

      const touches = e.evt.touches;
      const touch1 = touches[0];
      const touch2 = touches[1];

      const stage = stageRef.current;
      if (!stage) return;

      // --- Handle transition from 2 touches to 1 ---
      if (touches.length === 1 && dragStopped && transitionTouchInfo.current) {
        // Calculate the new stage position based on the remaining finger
        const currentTouch = touches[0];
        const { stagePosOnLift, touchPosOnLift, stageScaleOnLift } =
          transitionTouchInfo.current;

        // Calculate the movement of the remaining finger in screen coordinates
        const dx = currentTouch.clientX - touchPosOnLift.x;
        const dy = currentTouch.clientY - touchPosOnLift.y;

        // The new stage position is the position when the finger was lifted, plus the movement
        // Apply the movement at the scale when the finger was lifted
        const newPos = {
          x: stagePosOnLift.x + dx, // Apply screen space movement directly
          y: stagePosOnLift.y + dy, // Apply screen space movement directly
        };

        stage.setAttrs({
          x: newPos.x,
          y: newPos.y,
        });

        stage.batchDraw();

        // Reset transition info
        transitionTouchInfo.current = null;
        setDragStopped(false); // Allow Konva drag to take over from this new position
        stage.startDrag(); // Restart Konva's drag from this new position
        return; // Stop processing this touchmove event further
      }

      // --- Handle two touches (pinch/pan) ---
      if (touch1 && touch2) {
        setIsPanPinching(true);
        // If Konva is dragging with one finger, stop it for multi-touch
        if (stage.isDragging()) {
          stage.stopDrag();
          setDragStopped(true); // Indicate that we stopped Konva's drag
        }

        const p1 = {
          x: touch1.clientX,
          y: touch1.clientY,
        };
        const p2 = {
          x: touch2.clientX,
          y: touch2.clientY,
        };

        const newCenter = getCenter(p1, p2);
        const dist = getDistance(p1, p2);

        if (!lastCenter || !lastDist) {
          setLastCenter(newCenter);
          setLastDist(dist);
          return;
        }

        // local coordinates of center point relative to the stage with old scale
        const oldScale = stage.scaleX();
        const pointTo = {
          x: (lastCenter.x - stage.x()) / oldScale, // Using lastCenter as reference
          y: (lastCenter.y - stage.y()) / oldScale, // Using lastCenter as reference
        };

        // ---- MODIFICATION FOR LESS AGGRESSIVE ZOOM ----
        const ZOOM_DAMPING_FACTOR = 0.7; // 30% less aggressive
        const rawZoomRatio = dist / lastDist;
        const effectiveZoomRatio = 1 + (rawZoomRatio - 1) * ZOOM_DAMPING_FACTOR;
        const scale = oldScale * effectiveZoomRatio;

        // Cap scale
        const wouldBeScale = calculateScalePercentage(scale);

        if (
          wouldBeScale > SCALE_LIMITS.MAX ||
          wouldBeScale < SCALE_LIMITS.MIN
        ) {
          // Still update lastCenter/lastDist for smoother stopping even if scale is capped
          setLastDist(dist);
          setLastCenter(newCenter);
          return;
        }

        // calculate new position of the stage
        // This calculation combines scaling around the old center's stage point
        // and translating based on the movement of the center point.
        const dx = newCenter.x - lastCenter.x;
        const dy = newCenter.y - lastCenter.y;

        const newPos = {
          x: newCenter.x - pointTo.x * scale + dx,
          y: newCenter.y - pointTo.y * scale + dy,
        };

        stage.setAttrs({
          scaleX: scale,
          scaleY: scale,
          x: newPos.x,
          y: newPos.y,
        });

        stage.batchDraw();
        setScale(scale);

        // Update last values for the next move event
        setLastDist(dist);
        setLastCenter(newCenter);
      }
    },
    [
      dragStopped,
      lastCenter,
      lastDist,
      stageRef,
      setScale,
      calculateScalePercentage,
      SCALE_LIMITS,
    ]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      const stage = stageRef.current;
      if (!stage) {
        setLastDist(0);
        setLastCenter(null);
        setDragStopped(false);
        transitionTouchInfo.current = null; // Ensure reset
        resetInteractionState();
        return;
      }

      const touches = e.evt.touches; // These are the *remaining* touches *after* the one that ended
      if (touches.length === 1 && dragStopped) {
        // One finger was just lifted, and we were in a two-finger gesture
        const remainingTouch = touches[0];
        transitionTouchInfo.current = {
          stagePosOnLift: { x: stage.x(), y: stage.y() },
          touchPosOnLift: {
            x: remainingTouch.clientX,
            y: remainingTouch.clientY,
          },
          stageScaleOnLift: stage.scaleX(), // Store scale for potential use, though not used in current simplified pos calc
        };
      } else {
        // No touches left, or the gesture wasn't a 2->1 transition after stopping drag
        setLastDist(0); // Reset distance
        setLastCenter(null); // Reset center
        setDragStopped(false); // Reset drag stop flag
        transitionTouchInfo.current = null; // Ensure reset

        // If there are no touches left, stop Konva's drag just in case
        if (stage.isDragging()) {
          stage.stopDrag();
        }
      }

      resetInteractionState();
    },
    [dragStopped, stageRef]
  );

  // Clean up function for your useEffect
  const cleanup = () => {
    if (wheelTimeout.current) {
      cancelAnimationFrame(wheelTimeout.current);
    }
    accumulatedDelta.current = 0;
    isZooming.current = false;
    lastWheelTime.current = 0;
    // Also reset touch state refs on cleanup
    transitionTouchInfo.current = null;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Cache the image so the canvas does not need to redraw this
  // Major improvement in time spent painting
  useEffect(() => {
    if (imageLayerRef.current && status === "loaded") {
      imageLayerRef.current.cache({ pixelRatio: 0.5 });
      imageLayerRef.current.batchDraw();
      //   setBackgroundLoaded(true);
    } else if (status === "loading") {
      // setBackgroundLoaded(false);
    } else if (status === "error") {
      // setBackgroundLoaded(true);
    }
  }, [image, originalWidth, originalHeight, status, loadingComplete]); // setLoading

  if (!loadingComplete) {
    return (
      <div className='d-flex align-items-center justify-content-center full-height full-width'>
        <Spinner variant='primary' size='lg' />
      </div>
    );
  }

  if (mapError) {
    return (
      <div className='d-flex align-items-center justify-content-center full-height full-width'>
        <span>Cannot display map...</span>
      </div>
    );
  }

  return (
    <Stage
      listening={!isInteracting}
      draggable={!isPanPinching}
      ref={stageRef}
      width={width}
      height={height}
      onWheel={handleWheel}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      offset={{ x: offset.x, y: offset.y }}
    >
      {/* Background image */}
      <Layer ref={imageLayerRef} listening={false}>
        <Rect
          width={originalWidth}
          height={originalHeight}
          fillPatternImage={image}
          fillPatternRepeat='no-repeat'
          listening={false}
        />
      </Layer>
      <Seats data={data} />
      <Sections data={data} />
    </Stage>
  );
}
